import type { AdminApiUser } from '@/types/serializers';
import { useState } from 'react';
import { AdminButton } from '../../components/AdminButton';
import AdminBackLink from '../../components/atoms/AdminBackLink';
import AdminErrorMessage from '../../components/atoms/AdminErrorMessage';
import AdminFormInput from '../../components/atoms/AdminFormInput';
import useFetchAdminUsers from '../../api/useFetchAdminUsers';
import useUpdateAdminUser from '../../api/useUpdateAdminUser';
import useResetAdminUserPassword from '../../api/useResetAdminUserPassword';
import AdminLoadingState from '../../components/molecules/AdminLoadingState';
import AdminErrorState from '../../components/molecules/AdminErrorState';

type AdminUserEditPageProps = {
  userId: number;
};

const ROLE_OPTIONS = [
  { value: 'admin', label: '管理者ユーザー' },
  { value: 'marker_manager', label: '通常ユーザー' },
] as const;

export default function AdminUserEditPage({ userId }: AdminUserEditPageProps) {
  const { data: users, isLoading, isError } = useFetchAdminUsers();
  const user = users?.find((u: AdminApiUser) => u.id === userId);

  if (isLoading) return <AdminLoadingState />;
  if (isError || !user) return <AdminErrorState message="ユーザー情報の読み込みに失敗しました。" />;

  return (
    <div className="flex flex-col gap-4">
      <AdminBackLink to="/admin" search={{ tab: 'users' }} />
      <div className="rounded-md p-md shadow-[2px_2px_4px_0px_rgba(0,0,0,0.1)]">
        <div className="bg-white">
          <SectionHeader title="ユーザーの編集" />
          <div className="mt-10 px-4">
            <ProfileSection user={user} />
            <div className="my-6 h-px bg-border" />
            <PasswordSection userId={user.id} />
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="flex flex-col gap-3.5">
      <h2 className="my-0 pl-4 text-base font-bold text-[#4188ff]">{title}</h2>
      <div className="h-px w-full bg-[#4188ff]" />
    </div>
  );
}

function ProfileSection({ user }: { user: AdminApiUser }) {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [role, setRole] = useState(
    user.role === 'super_admin' || user.role === 'admin' ? 'admin' : 'marker_manager'
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { mutateAsync: updateUser, isPending } = useUpdateAdminUser();

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('名前を入力してください');
      return;
    }
    if (!email.trim()) {
      setError('メールアドレスを入力してください');
      return;
    }
    try {
      setError(null);
      setSuccess(false);
      await updateUser({ id: user.id, name: name.trim(), email: email.trim(), role });
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : '更新に失敗しました');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="w-[400px]">
        <AdminFormInput
          label="名前"
          required
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setSuccess(false);
          }}
        />
      </div>
      <div className="w-[400px]">
        <AdminFormInput
          label="メールアドレス"
          required
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setSuccess(false);
          }}
        />
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-sm font-bold text-[#333]">アクセス権限</span>
        <div className="flex gap-4">
          {ROLE_OPTIONS.map((opt) => (
            <label key={opt.value} className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="role"
                value={opt.value}
                checked={role === opt.value}
                onChange={() => {
                  setRole(opt.value);
                  setSuccess(false);
                }}
                className="size-4 accent-[#4188ff]"
              />
              <span className="text-sm text-main">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>
      <AdminErrorMessage message={error} />
      {success && <p className="text-sm text-[#38b17c]">プロフィールを更新しました。</p>}
      <div className="flex justify-end">
        <AdminButton
          label={isPending ? '保存中...' : '保存する'}
          disabled={isPending}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
}

function PasswordSection({ userId }: { userId: number }) {
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const { mutateAsync: resetPassword, isPending } = useResetAdminUserPassword();

  const hasInput = password.length > 0 || passwordConfirmation.length > 0;

  const handleSubmit = async () => {
    if (!password) {
      setError('新規パスワードを入力してください');
      return;
    }
    if (password !== passwordConfirmation) {
      setError('パスワードが一致しません');
      return;
    }
    try {
      setError(null);
      setSuccess(false);
      await resetPassword({ id: userId, password, passwordConfirmation });
      setPassword('');
      setPasswordConfirmation('');
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'パスワードの更新に失敗しました');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="w-[604px]">
        <AdminFormInput
          label="新規パスワード"
          type="password"
          placeholder="新規パスワードを入力してください"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setSuccess(false);
          }}
        />
      </div>
      <div className="w-[604px]">
        <AdminFormInput
          label="新規パスワード（確認用）"
          type="password"
          placeholder="新規パスワードを入力してください"
          value={passwordConfirmation}
          onChange={(e) => {
            setPasswordConfirmation(e.target.value);
            setSuccess(false);
          }}
        />
      </div>
      <AdminErrorMessage message={error} />
      {success && <p className="text-sm text-[#38b17c]">パスワードを更新しました。</p>}
      <div className="flex justify-end">
        <AdminButton
          label={isPending ? '保存中...' : '保存する'}
          disabled={isPending || !hasInput}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
}
